import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useController, type FieldError } from "react-hook-form";
import { Input, InputGroup, InputSuffixButton } from "../ui/input";
import { Textarea, TextareaFooter, TextareaGroup } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge, type badgeVariantProps } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  SearchField,
  SearchFieldInput,
  SearchFieldClearButton,
} from "@/components/designSystem/SearchField";
import { DropdownScrollArea } from "./DropdownScrollArea";
import { SegmentedTabs } from "./SegmentedTabs";
import { DateRangePicker, type DateRange } from "./DateRangePicker";
import { DayCell } from "./DatePicker";
import {
  MONTH_OPTIONS,
  WEEKDAYS,
  buildCalendarCells,
  formatDateOnly,
  getYearOptions,
  isSameDay,
  isToday,
  useCalendarView,
  useScrollToSelectedItem,
  type DayStatus,
} from "./calendarCore";
import { formatDateToString, parseStringToDate } from "@/common/utils/date";
import {
  formatCardNumber,
  normalizeCardNumber,
  CARD_NUMBER_MAX_LENGTH,
  formatPhone,
  normalizePhone,
  formatBusinessRegistrationNumber,
  normalizeBusinessRegistrationNumber,
} from "@/common/utils/format";
import { cn } from "@/lib/utils";
import { useFieldRef } from "@/hooks/useFieldRef";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Skeleton } from "../ui/skeleton";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "@/components/icons";

/**
 * 조회 모드(disabled)에서 값을 텍스트로 보이는 자리.
 * 모든 폼 필드가 같은 높이(24px)·서체로 표기하도록 한곳에 둔다.
 */
export function FormReadOnlyValue({
  children,
  className,
  onClick,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <span
      className={cn(
        "flex-1 h-6 flex items-center typo-body-2-medium text-labels-primary gap-2",
        className,
      )}
      onClick={onClick}
    >
      {children}
    </span>
  );
}

/** 폼 필드 행 레이아웃 */
export function FormRow({
  icon,
  label,
  children,
  error,
  isLoading = false,
  tooltip,
  required = false,
  align = "center",
  readOnly = false,
}: {
  icon: React.ReactNode;
  label: string;
  children?: React.ReactNode;
  error?: FieldError | boolean;
  isLoading?: boolean;
  tooltip?: React.ReactNode;
  required?: boolean;
  /** 여러 줄 입력(textarea 등)은 "start" 로 라벨을 첫 줄에 맞춘다 */
  align?: "center" | "start";
  /** start 정렬이지만 값이 텍스트뿐인 조회 모드 — 라벨을 24px 텍스트 줄에 맞춘다 */
  readOnly?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex gap-2 items-center w-full">
        <div className="flex items-center gap-1.5 h-6 shrink-0">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="w-30 h-4 rounded" />
        </div>
        <Skeleton className="flex-1 h-5 rounded" />
      </div>
    );
  }

  const currentLable = required ? label + "*" : label;
  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        className={cn(
          "flex gap-2 w-full",
          align === "start" ? "items-start" : "items-center",
        )}
      >
        <div
          className={cn(
            "flex items-center gap-1.5 shrink-0",
            // start 정렬일 때는 컨트롤 첫 줄(32px)에 라벨을 맞춘다
            align === "start" && !readOnly ? "h-8" : "h-6",
          )}
        >
          {icon}
          <span className="typo-body-2-medium text-labels-alternative w-30 flex gap-1 items-center">
            {currentLable}
            {tooltip}
          </span>
        </div>
        {/* min-w-0 — 컨트롤 고유폭이 남은 폭보다 크면 행 밖으로 삐져나온다(수정 Drawer 등) */}
        <div className="flex w-full min-w-0">{children}</div>
      </div>
      {error && (
        <span className="ml-37.5 text-accent-mute-red typo-caption-1-medium">
          {typeof error === "object" && error?.message
            ? error.message
            : "필수 입력 항목이에요."}
        </span>
      )}
    </div>
  );
}

/** 읽기 전용 텍스트 필드 (수정 불가) */
export function FormText({
  value,
  icon,
  label,
  isLoading = false,
  disabled = false,
  required = false,
  placeholder,
}: {
  value?: string;
  icon: React.ReactNode;
  label: string;
  isLoading?: boolean;
  disabled?: boolean;
  required?: boolean;
  /** 값이 없을 때 표시할 안내 문구 (예: "충전기 선택 시 자동 입력됩니다.") */
  placeholder?: string;
}) {
  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    return (
      <FormRow icon={icon} label={label}>
        <FormReadOnlyValue>{value || "-"}</FormReadOnlyValue>
      </FormRow>
    );
  }

  return (
    <FormRow icon={icon} label={label} required={required}>
      {/* bg 를 주지 않는다 — InputGroup 의 className 은 바깥 컨테이너에 붙는데
          내부 wrapper 가 disabled 변형으로 이미 배경을 칠해 두 겹으로 겹친다 */}
      <InputGroup size="inline" disabled className="flex-1">
        <Input value={value ?? ""} placeholder={placeholder} readOnly />
      </InputGroup>
    </FormRow>
  );
}

/** 입력 포맷 설정 */
interface InputFormatConfig {
  /** 입력값을 포맷된 문자열로 변환 */
  format: (value: string) => string;
  /** 포맷된 문자열에서 저장할 값으로 변환 (기본: 포맷된 값 그대로 저장) */
  parse?: (value: string) => string;
  /** 포맷된 값의 최대 길이 */
  maxLength?: number;
}

/** 소수점 허용 숫자 포맷 생성 */
function createDecimalFormat(
  decimalPlaces: number,
  maxLength = 20,
): InputFormatConfig {
  const formatDecimal = (value: string): string => {
    const cleaned = value.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    const integer = parts[0] || "";
    if (parts.length <= 1) return integer;
    const decimal = parts[1]?.slice(0, decimalPlaces) ?? "";
    return `${integer}.${decimal}`;
  };
  return { format: formatDecimal, parse: formatDecimal, maxLength };
}

/** 미리 정의된 입력 포맷 */
export const INPUT_FORMATS = {
  phone: {
    format: formatPhone,
    parse: normalizePhone,
    maxLength: 13,
  },
  number: {
    format: (value: string) => value.replace(/\D/g, ""),
    parse: (value: string) => value.replace(/\D/g, ""),
    maxLength: 16,
  },
  govConnectorId: {
    format: (value: string) => value.replace(/\D/g, ""),
    parse: (value: string) => value.replace(/\D/g, ""),
    maxLength: 2,
  },
  /** 한전 고객번호 (##-####-####) */
  kepcoCustomerNumber: {
    format: (value: string) => {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      if (digits.length <= 2) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    },
    parse: (value: string) => value.replace(/\D/g, "").slice(0, 10),
    maxLength: 12,
  },
  /** 소수점 2자리까지 허용 (예: 123.45) */
  decimal: createDecimalFormat(2),
  /** 소수점 1자리까지 허용 (예: 123.4) */
  decimal1: createDecimalFormat(1),
  /** 영문 대문자 2자리 (소문자 → 대문자 자동 변환) */
  upperAlpha2: {
    format: (value: string) =>
      value
        .replace(/[^A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 2),
    parse: (value: string) =>
      value
        .replace(/[^A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 2),
    maxLength: 2,
  },
  /** 충전카드 번호 (영문/숫자만, 4자리마다 "-") */
  cardNumber: {
    format: formatCardNumber,
    parse: normalizeCardNumber,
    // 원문 + 4자리마다 삽입되는 "-" 개수
    maxLength:
      CARD_NUMBER_MAX_LENGTH + Math.floor((CARD_NUMBER_MAX_LENGTH - 1) / 4),
  },
  /** 사업자 등록번호 (###-##-#####) */
  businessRegistrationNumber: {
    format: formatBusinessRegistrationNumber,
    parse: normalizeBusinessRegistrationNumber,
    maxLength: 12,
  },
} satisfies Record<string, InputFormatConfig>;

export type InputFormatType = keyof typeof INPUT_FORMATS;

/** 텍스트 입력 필드 */
export function FormTextInput({
  name,
  icon,
  label,
  placeholder,
  disabled = false,
  nullable = false,
  isLoading = false,
  formatType,
  disableSpaces = false,
  maxLength,
  required = false,
  renderDisabledValue,
  suffixButton,
}: {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  disabled?: boolean;
  nullable?: boolean;
  isLoading?: boolean;
  /** 입력 포맷 타입 (예: "phone") */
  formatType?: InputFormatType;
  /** 공백 입력 차단 */
  disableSpaces?: boolean;
  /** 최대 입력 글자수 */
  maxLength?: number;
  /** 필수 항목 표시 (*) */
  required?: boolean;
  /** disabled 상태에서 커스텀 렌더링 */
  renderDisabledValue?: (displayValue: string) => React.ReactNode;
  /** 입력 우측에 붙는 액션 버튼 (예: "이력 조회") */
  suffixButton?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
}) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  const fieldRef = useFieldRef(field);

  const formatConfig = formatType ? INPUT_FORMATS[formatType] : null;

  const displayValue = formatConfig
    ? formatConfig.format(field.value ?? "")
    : (field.value ?? "");

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    return (
      <FormRow icon={icon} label={label}>
        <FormReadOnlyValue>
          {renderDisabledValue
            ? renderDisabledValue(displayValue)
            : displayValue || "-"}
        </FormReadOnlyValue>
      </FormRow>
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      error={invalid && error}
      required={required}
    >
      <InputGroup size="inline" className="flex-1" error={invalid}>
        <Input
          ref={fieldRef}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          maxLength={maxLength ?? formatConfig?.maxLength}
          onChange={(e) => {
            let raw = e.target.value;
            if (disableSpaces) {
              raw = raw.replace(/\s/g, "");
            }
            if (formatConfig) {
              const parsed = formatConfig.parse ? formatConfig.parse(raw) : raw;
              field.onChange(nullable && !parsed ? null : parsed);
            } else {
              field.onChange(nullable && !raw ? null : raw);
            }
          }}
          onBlur={field.onBlur}
        />
        {suffixButton && (
          <InputSuffixButton
            disabled={suffixButton.disabled}
            onClick={suffixButton.onClick}
            className="min-w-16 max-w-21 px-4"
          >
            {suffixButton.label}
          </InputSuffixButton>
        )}
      </InputGroup>
    </FormRow>
  );
}

/**
 * 라벨 행 없이 제목만 크게 입력하는 필드 (문서 제목형).
 * 섹션 제목 바로 아래에 전체 너비로 놓인다 — 라벨·아이콘이 없으므로 FormRow 를 쓰지 않는다.
 */
export function FormTitleInput({
  name,
  placeholder,
  disabled = false,
  nullable = false,
  isLoading = false,
  maxLength,
}: {
  name: string;
  placeholder: string;
  disabled?: boolean;
  nullable?: boolean;
  isLoading?: boolean;
  /** 최대 입력 글자수 */
  maxLength?: number;
}) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  const fieldRef = useFieldRef<HTMLInputElement>(field);

  if (isLoading) {
    return <Skeleton className="h-12 w-full rounded-xl" />;
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* InputGroup 을 쓰지 않는다 — 내부 Input 이 typo-body-1-medium 을 항상 붙여
          제목용 typo-heading-1-bold 와 CSS 순서로 충돌한다 */}
      <input
        ref={fieldRef}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        value={field.value ?? ""}
        maxLength={maxLength}
        onChange={(e) => {
          const raw = e.target.value;
          field.onChange(nullable && !raw ? null : raw);
        }}
        onBlur={field.onBlur}
        className={cn(
          "w-full h-12 px-4 py-3 rounded-xl bg-static-white outline-none",
          "typo-heading-1-bold text-labels-primary placeholder:text-labels-assistive",
          "disabled:bg-fills-neutral disabled:cursor-not-allowed",
          invalid
            ? "inset-border-error focus:inset-border-error-focus"
            : "inset-border-solid-normal focus:inset-border-focus",
        )}
      />
      {invalid && error && (
        <span className="text-accent-mute-red typo-caption-1-medium">
          {error.message ?? "필수 입력 항목이에요."}
        </span>
      )}
    </div>
  );
}

/** 셀렉트 입력 필드 */
export function FormSelectInput({
  name,
  icon,
  label,
  placeholder,
  options,
  disabled = false,
  isLoading = false,
  showSearch = false,
  searchPlaceholder = "검색어를 입력하세요...",
  required = false,
  onValueChange,
}: {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  isLoading?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  required?: boolean;
  /** 값 변경 후 추가 콜백 (예: 종속 필드 초기화) */
  onValueChange?: (value: string) => void;
}) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  const fieldRef = useFieldRef(field);

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    const selectedOption = options.find((opt) => opt.value === field.value);
    return (
      <FormRow icon={icon} label={label}>
        <FormReadOnlyValue>{selectedOption?.label || "-"}</FormReadOnlyValue>
      </FormRow>
    );
  }

  if (showSearch) {
    return (
      <FormSearchableSelect
        name={name}
        icon={icon}
        label={label}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        options={options}
        field={field}
        fieldRef={fieldRef}
        invalid={invalid}
        error={error}
        required={required}
        onValueChange={onValueChange}
      />
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      error={invalid && error}
      required={required}
    >
      <FormSelectControl
        name={name}
        placeholder={placeholder}
        options={options}
        onValueChange={onValueChange}
      />
    </FormRow>
  );
}

/**
 * 라벨 없는 셀렉트. 한 라벨 아래 여러 셀렉트를 두는 행(예: 한전 요금제의
 * 계약종별→요금제)에서 `FormRow` 의 children 으로 쓴다.
 */
export function FormSelectControl({
  name,
  placeholder,
  options,
  disabled = false,
  onValueChange,
}: {
  name: string;
  placeholder: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  onValueChange?: (value: string) => void;
}) {
  const { field, fieldState } = useController({ name });
  const { invalid } = fieldState;
  const fieldRef = useFieldRef(field);

  return (
    <div className="flex-1">
      <Select
        name={name}
        // null 을 그대로 넘기면 Radix 가 placeholder 를 그리지 않고 빈 칸으로 남는다
        value={field.value ?? undefined}
        onValueChange={(value) => {
          field.onChange(value);
          onValueChange?.(value);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          ref={fieldRef}
          name={name}
          className={cn(
            "w-full bg-static-white rounded-sm px-2 typo-body-2-medium",
            invalid && "inset-border-error",
          )}
          triggerColor="labels-alternative"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}

/** 검색 가능한 셀렉트 (Popover 기반) */
function FormSearchableSelect({
  name,
  icon,
  label,
  placeholder,
  searchPlaceholder,
  options,
  field,
  fieldRef,
  invalid,
  error,
  required = false,
  onValueChange,
}: {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  options: { value: string; label: string }[];
  field: ReturnType<typeof useController>["field"];
  fieldRef: React.RefObject<HTMLElement | null>;
  invalid: boolean;
  error?: ReturnType<typeof useController>["fieldState"]["error"];
  required?: boolean;
  onValueChange?: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const selectedOption = options.find((opt) => opt.value === field.value);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);

  return (
    <FormRow
      icon={icon}
      label={label}
      error={invalid && error}
      required={required}
    >
      <div className="flex-1">
        <PopoverPrimitive.Root
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) setSearchQuery("");
          }}
        >
          <PopoverPrimitive.Trigger asChild>
            <button
              ref={fieldRef as React.RefObject<HTMLButtonElement>}
              type="button"
              name={name}
              className={cn(
                "flex w-full h-8 items-center justify-between inset-border-solid-normal bg-static-white rounded-sm px-2",
                "typo-body-2-medium",
                invalid && "inset-border-error",
              )}
            >
              <span
                className={cn(
                  "truncate",
                  selectedOption
                    ? "text-labels-primary"
                    : "text-labels-assistive",
                )}
              >
                {selectedOption?.label || placeholder}
              </span>
              <ChevronDown
                className="size-6"
                data-slot="select-icon"
                color="labels-alternative"
              />
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              className={cn(
                "bg-static-white text-labels-primary",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
                "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                "z-50 w-(--radix-popover-trigger-width) overflow-hidden rounded-xl",
                "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
              )}
              sideOffset={4}
              align="start"
            >
              {/* 검색 입력 */}
              <div className="px-2 pt-3 justify-between">
                <SearchField>
                  <SearchFieldInput
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <SearchFieldClearButton />
                </SearchField>
              </div>

              {/* 옵션 목록 — 스크롤바는 목록 위에 겹쳐 그린다(폭을 빼앗지 않게) */}
              <DropdownScrollArea
                className="max-h-70"
                contentClassName="flex flex-col gap-1 px-2 py-3"
                onWheel={(e) => e.stopPropagation()}
              >
                {filteredOptions.length === 0 ? (
                  <span className="px-2 py-1 typo-label-1-medium text-labels-assistive">
                    검색 결과가 없습니다.
                  </span>
                ) : (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        field.onChange(option.value);
                        onValueChange?.(option.value);
                        setOpen(false);
                        setSearchQuery("");
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-lg h-8 px-2 py-1",
                        "cursor-pointer outline-none select-none",
                        "hover:bg-fills-primary focus:bg-fills-primary",
                        "typo-label-1-semibold text-labels-primary",
                      )}
                    >
                      <span className="truncate">{option.label}</span>
                      {field.value === option.value && (
                        <Check size={20} color="accent-mute-green" />
                      )}
                    </button>
                  ))
                )}
              </DropdownScrollArea>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </div>
    </FormRow>
  );
}

/** 셀렉트 입력 필드 */
export function FormDependentSelectInput({
  name,
  icon,
  label,
  placeholder,
  options,
  depProps,
  onValueChange,
  disabled = false,
  isLoading = false,
  required = false,
}: {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  options: { value: string; label: string }[];
  depProps: unknown;
  /** 사용자 선택뿐 아니라 depProps 변경으로 값이 비워질 때도 호출된다 */
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  const fieldRef = useFieldRef(field);
  const prevDepPropsRef = React.useRef(depProps);
  React.useEffect(() => {
    if (!depProps) return;

    if (prevDepPropsRef.current !== depProps) {
      field.onChange("");
      onValueChange?.("");
      prevDepPropsRef.current = depProps;
    }
  }, [depProps, field, onValueChange]);

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    const selectedOption = options.find((opt) => opt.value === field.value);
    return (
      <FormRow icon={icon} label={label}>
        <FormReadOnlyValue>{selectedOption?.label || "-"}</FormReadOnlyValue>
      </FormRow>
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      error={invalid && error}
      required={required}
    >
      <div className="flex-1">
        <Select
          name={name}
          value={field.value}
          onValueChange={(value) => {
            field.onChange(value);
            onValueChange?.(value);
          }}
          disabled={!depProps}
        >
          <SelectTrigger
            ref={fieldRef}
            name={name}
            className={cn(
              "w-full bg-static-white rounded-sm px-2 typo-body-2-medium",
              invalid && "inset-border-error",
            )}
            triggerColor="labels-alternative"
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </FormRow>
  );
}

export function FormDateRangePicker({
  startName,
  endName,
  placeholder,
  disabled = false,
  showQuickOptions,
}: {
  startName: string;
  endName: string;
  placeholder: string;
  disabled?: boolean;
  showQuickOptions?: boolean;
}) {
  const startField = useController({ name: startName });
  const endField = useController({ name: endName });

  const dateRange: DateRange = {
    startDate: parseStringToDate(startField.field.value),
    endDate: parseStringToDate(endField.field.value),
  };

  const handleChange = (range: DateRange) => {
    startField.field.onChange(formatDateToString(range.startDate));
    endField.field.onChange(formatDateToString(range.endDate));
  };

  if (disabled) {
    const hasDateRange = dateRange.startDate || dateRange.endDate;
    // 저장값이 "YYYY-MM-DD HH:mm" 형태일 수 있어 날짜 부분만 자른다
    const dateOnly = (val: string | null | undefined) =>
      val ? val.slice(0, 10) : "-";
    const displayText = hasDateRange
      ? `${dateOnly(startField.field.value)} ~ ${dateOnly(endField.field.value)}`
      : "-";

    return (
      <FormReadOnlyValue>{displayText}</FormReadOnlyValue>
    );
  }

  return (
    <div className="flex-1">
      <DateRangePicker
        name={startName}
        placeholder={
          dateRange.startDate === null && dateRange.endDate === null
            ? placeholder
            : undefined
        }
        placeholderStyle="typo-body-2-medium text-labels-assistive"
        value={dateRange}
        onChange={handleChange}
        showCalendarIcon={false}
        showArrowIcon={false}
        showQuickOptions={showQuickOptions}
        disabled={disabled}
        className={cn(
          "w-full justify-start h-8 bg-static-white rounded-sm px-2",
          ((startField.fieldState.invalid && startField.fieldState.error) ||
            (endField.fieldState.invalid && endField.fieldState.error)) &&
            "inset-border-error",
        )}
      />
    </div>
  );
}

export function FormDatePicker({
  dateName,
  placeholder,
  disabled = false,
  size = "sm",
  minDate,
}: {
  dateName: string;
  placeholder: string;
  disabled?: boolean;
  /** 기본 32px, `md` 는 전체 화면 폼의 48px 입력과 같은 크기 */
  size?: "sm" | "md";
  /** 이 날짜보다 이전은 흐리게 두고 고를 수 없다 */
  minDate?: Date;
}) {
  const { field, fieldState } = useController({ name: dateName });
  const fieldRef = useFieldRef<HTMLButtonElement>(field);
  const [open, setOpen] = React.useState(false);
  const dateValue = parseStringToDate(field.value);
  const hasValue = dateValue !== null;

  const {
    viewYear,
    viewMonth,
    setViewYear,
    setViewMonth,
    focus,
    goPrevMonth,
    goNextMonth,
  } = useCalendarView(dateValue);
  const [yearSelectOpen, setYearSelectOpen] = React.useState(false);
  const [monthSelectOpen, setMonthSelectOpen] = React.useState(false);
  const yearListRef = React.useRef<HTMLDivElement>(null);
  const monthListRef = React.useRef<HTMLDivElement>(null);

  useScrollToSelectedItem(yearSelectOpen, yearListRef);
  useScrollToSelectedItem(monthSelectOpen, monthListRef);

  // 열릴 때 선택값(없으면 오늘) 기준으로 달력을 맞춘다 — effect 가 아니라 열기 이벤트에서
  const handleOpenChange = (next: boolean) => {
    if (next) focus(dateValue);
    setOpen(next);
  };

  const isBeforeMin = (date: Date) => {
    if (!minDate) return false;
    const floor = new Date(minDate);
    floor.setHours(0, 0, 0, 0);
    return date.getTime() < floor.getTime();
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(viewYear, viewMonth, day);
    if (isBeforeMin(newDate)) return;
    field.onChange(formatDateToString(newDate));
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    field.onChange(null);
    setOpen(false);
  };

  if (disabled) {
    return (
      <FormReadOnlyValue>
        {field.value ? field.value.slice(0, 10) : "-"}
      </FormReadOnlyValue>
    );
  }

  const dayStatus = (date: Date): DayStatus => {
    if (isSameDay(date, dateValue)) return "selected";
    if (isBeforeMin(date)) return "assistive";
    if (isToday(date)) return "today";
    return "normal";
  };

  const calendarDays = buildCalendarCells(viewYear, viewMonth, dayStatus);
  const yearOptions = getYearOptions();
  const monthOptions = MONTH_OPTIONS;

  return (
    <div className="flex-1">
      <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={fieldRef}
            type="button"
            className={cn(
              "w-full flex items-center gap-1",
              size === "md" ? "h-12 px-4 rounded-xl" : "h-8 px-2 rounded-sm",
              "inset-border-solid-normal bg-static-white",
              "cursor-pointer",
              fieldState.invalid && fieldState.error && "inset-border-error",
            )}
          >
            <span
              className={cn(
                "flex-1 text-left typo-body-2-medium",
                hasValue ? "text-labels-primary" : "text-labels-assistive",
              )}
            >
              {hasValue && dateValue ? formatDateOnly(dateValue) : placeholder}
            </span>
            {hasValue ? (
              <div
                onClick={handleClear}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                className="flex items-center justify-center cursor-pointer"
              >
                <X size={24} color="labels-alternative" />
              </div>
            ) : (
              <ChevronDown size={24} color="labels-alternative" />
            )}
          </button>
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              "bg-static-white text-labels-primary",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "z-50 w-60 overflow-hidden rounded-xl",
              "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
            )}
            sideOffset={4}
            align="start"
          >
            <div className="px-2 py-3">
              {/* 헤더: 년/월 선택 및 네비게이션 */}
              <div className="flex items-center justify-between px-1.5 mb-2.5">
                <div className="flex items-center gap-1">
                  {/* 연도 선택 */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setYearSelectOpen(!yearSelectOpen);
                        setMonthSelectOpen(false);
                      }}
                      className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                    >
                      {viewYear}년
                      <ChevronDown size={16} color="labels-primary" />
                    </button>
                    {yearSelectOpen && (
                      <DropdownScrollArea
                        scrollerRef={yearListRef}
                        className={cn(
                          "absolute top-full left-0 mt-1",
                          "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                          "z-100 max-h-48",
                        )}
                        contentClassName="p-1"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {yearOptions.map((year) => (
                          <button
                            key={year}
                            type="button"
                            data-selected={year === viewYear}
                            onClick={() => {
                              setViewYear(year);
                              setYearSelectOpen(false);
                            }}
                            className={cn(
                              "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                              "hover:bg-fills-primary",
                              year === viewYear &&
                                "text-primary-normal bg-fills-highlight",
                            )}
                          >
                            {year}년
                          </button>
                        ))}
                      </DropdownScrollArea>
                    )}
                  </div>

                  {/* 월 선택 */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setMonthSelectOpen(!monthSelectOpen);
                        setYearSelectOpen(false);
                      }}
                      className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                    >
                      {viewMonth + 1}월
                      <ChevronDown size={16} color="labels-primary" />
                    </button>
                    {monthSelectOpen && (
                      <DropdownScrollArea
                        scrollerRef={monthListRef}
                        className={cn(
                          "absolute top-full left-0 mt-1",
                          "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                          "z-100 max-h-48",
                        )}
                        contentClassName="p-1"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {monthOptions.map((month) => (
                          <button
                            key={month}
                            type="button"
                            data-selected={month === viewMonth}
                            onClick={() => {
                              setViewMonth(month);
                              setMonthSelectOpen(false);
                            }}
                            className={cn(
                              "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                              "hover:bg-fills-primary",
                              month === viewMonth &&
                                "text-primary-normal bg-fills-highlight",
                            )}
                          >
                            {month + 1}월
                          </button>
                        ))}
                      </DropdownScrollArea>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={goPrevMonth}
                    className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                  >
                    <ChevronLeft size={20} color="labels-primary" />
                  </button>
                  <button
                    type="button"
                    onClick={goNextMonth}
                    className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                  >
                    <ChevronRight size={20} color="labels-primary" />
                  </button>
                </div>
              </div>

              {/* 요일 헤더 */}
              <div className="grid grid-cols-7">
                {WEEKDAYS.map((day) => (
                  <div
                    key={day}
                    className="size-8 flex items-center justify-center typo-label-1-medium text-labels-alternative"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* 날짜 그리드 */}
              <div className="grid grid-cols-7">
                {calendarDays.map((dayInfo, index) => (
                  <DayCell
                    key={index}
                    day={dayInfo.day}
                    status={dayInfo.status}
                    onClick={() => {
                      if (dayInfo.isCurrentMonth) handleDateSelect(dayInfo.day);
                    }}
                  />
                ))}
              </div>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </div>
  );
}

/** 자동 높이 조절 textarea */
export function FormTextarea({
  name,
  placeholder,
  disabled = false,
  isLoading = false,
  maxLength,
}: {
  name: string;
  placeholder: string;
  disabled?: boolean;
  isLoading?: boolean;
  /** 최대 입력 글자수 */
  maxLength?: number;
}) {
  const { field } = useController({ name });
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const newHeight = Math.min(textarea.scrollHeight, 248);
    textarea.style.height = `${newHeight}px`;
  }, []);

  // 브라우저가 레이아웃을 계산한 후 다음 프레임에서 높이 조정
  React.useLayoutEffect(() => {
    const frameId = requestAnimationFrame(() => {
      adjustHeight();
    });
    return () => cancelAnimationFrame(frameId);
  }, [field.value, adjustHeight, disabled]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    field.onChange(e.target.value || null);
    adjustHeight();
  };

  if (isLoading) {
    return <Skeleton className="w-full h-24 rounded-xl" />;
  }

  return (
    <div className={"w-full p-6 bg-bg-alternative rounded-xl"}>
      <textarea
        id="memo"
        ref={(el) => {
          textareaRef.current = el;
          field.ref(el);
        }}
        placeholder={placeholder}
        value={field.value ?? ""}
        onChange={handleChange}
        onBlur={field.onBlur}
        disabled={disabled}
        rows={1}
        maxLength={maxLength}
        className={cn(
          "block max-h-42 w-full resize-none overflow-y-auto",
          "bg-transparent outline-none",
          "typo-body-2-medium text-labels-primary",
          "placeholder:text-labels-assistive",
          "disabled:cursor-not-allowed",
        )}
      />
    </div>
  );
}

/**
 * 라벨 행 + 테두리 + 글자수 카운터가 있는 textarea 입력 필드.
 * 메모용 `FormTextarea`(라벨 없는 회색 박스)와 달리 다른 Form*Input 과 같은 라벨 행을 쓴다.
 */
export function FormTextareaInput({
  name,
  icon,
  label,
  placeholder,
  maxLength,
  disabled = false,
  isLoading = false,
  required = false,
}: {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  /** 최대 입력 글자수 (카운터 표시 기준) */
  maxLength?: number;
  disabled?: boolean;
  isLoading?: boolean;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  const fieldRef = useFieldRef<HTMLTextAreaElement>(field);

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    return (
      <FormRow icon={icon} label={label} align="start" readOnly>
        <span className="flex-1 min-h-6 flex items-center typo-body-2-medium text-labels-primary whitespace-pre-wrap">
          {field.value || "-"}
        </span>
      </FormRow>
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      required={required}
      error={invalid && error}
      align="start"
    >
      <TextareaGroup
        error={invalid}
        maxLength={maxLength}
        className="flex-1 min-h-35 gap-1 rounded-md px-2 py-1.5"
      >
        <Textarea
          ref={fieldRef}
          name={name}
          placeholder={placeholder}
          value={field.value ?? ""}
          onChange={(e) => field.onChange(e.target.value)}
          onBlur={field.onBlur}
          // 디자인 실측: 기본 높이 140, 내용이 길어지면 208 까지 늘고 그 뒤 내부 스크롤
          className="min-h-27 max-h-52 typo-body-2-medium"
        />
        {/* 디자인에 상태 아이콘이 없어 카운터만 노출한다 */}
        <TextareaFooter showIcon={false} />
      </TextareaGroup>
    </FormRow>
  );
}

export interface RadioOption {
  /** 옵션 값 */
  value: string;
  /** 옵션 라벨 */
  label: string;
}

export interface FormRadioGroupProps {
  /** react-hook-form 필드 이름 */
  name: string;
  /** 라벨 앞에 표시할 아이콘 */
  icon: React.ReactNode;
  /** 필드 라벨 */
  label: string;
  /** 라디오 옵션 목록 */
  options: RadioOption[];
  /** 비활성화 여부 (true일 때 텍스트로 표시) */
  disabled?: boolean;
  /**
   * 사용자가 선택을 바꿀 때만 불린다. 폼 reset·기본값 주입으로는 불리지 않아
   * "사용자가 바꿨을 때 다른 필드도 함께 조정" 같은 반응에 쓴다.
   */
  onValueChange?: (value: string) => void;
  /** 라디오 그룹 추가 className */
  className?: string;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 필수 항목 표시 (*) */
  required?: boolean;
}

/**
 * 폼 라디오 그룹 컴포넌트
 * - react-hook-form과 통합된 라디오 버튼 그룹
 * - disabled 시 현재 값을 텍스트로 표시
 * - FormRow 레이아웃 적용
 */
export function FormRadioGroup({
  name,
  icon,
  label,
  options,
  disabled = false,
  isLoading = false,
  required = false,
  onValueChange,
}: FormRadioGroupProps) {
  const { field } = useController({ name });

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  return (
    <FormRow icon={icon} label={label} required={required}>
      <RadioGroup
        value={field.value}
        onValueChange={(value) => {
          field.onChange(value);
          onValueChange?.(value);
        }}
        disabled={disabled}
        // 조회 모드에서는 텍스트 행(FormReadOnlyValue, 24px)과 높이를 맞춘다 —
        // 32px 를 유지하면 이 행만 위아래 4px 씩 벌어져 행간이 어긋나 보인다
        className={cn("flex items-center gap-3", disabled ? "h-6" : "h-8")}
      >
        {options.map((option) => (
          <div key={option.value} className="flex items-center gap-1">
            <RadioGroupItem
              value={option.value}
              id={`${name}-${option.value}`}
            />
            <label
              htmlFor={`${name}-${option.value}`}
              className="typo-body-2-medium text-labels-primary cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </RadioGroup>
    </FormRow>
  );
}

// ============================================
// 세그먼트 탭 선택
// ============================================

export interface FormSegmentedTabsProps {
  /** react-hook-form 필드 이름 */
  name: string;
  /** 라벨 앞에 표시할 아이콘 */
  icon: React.ReactNode;
  /** 필드 라벨 */
  label: string;
  /** 탭 옵션 목록 */
  options: { value: string; label: string }[];
  /** 비활성화 여부 (true일 때 텍스트로 표시) */
  disabled?: boolean;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 필수 항목 표시 (*) */
  required?: boolean;
  /** 값 변경 후 추가 콜백 (예: 종속 필드 초기화) */
  onValueChange?: (value: string) => void;
}

/** 세그먼트 탭 선택 필드 (선택지가 2~3개로 적고 항상 노출해야 할 때 셀렉트 대신 사용) */
export function FormSegmentedTabs({
  name,
  icon,
  label,
  options,
  disabled = false,
  isLoading = false,
  required = false,
  onValueChange,
}: FormSegmentedTabsProps) {
  const { field, fieldState } = useController({ name });
  const { error, invalid } = fieldState;

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading />;
  }

  if (disabled) {
    const selectedOption = options.find((opt) => opt.value === field.value);
    return (
      <FormRow icon={icon} label={label}>
        <FormReadOnlyValue>{selectedOption?.label || "-"}</FormReadOnlyValue>
      </FormRow>
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      error={invalid && error}
      required={required}
    >
      <SegmentedTabs
        options={options}
        value={field.value ?? ""}
        // 다른 폼 입력(32px)과 높이를 맞춘다
        className="h-8"
        buttonClassName="h-8"
        onChange={(value) => {
          // 이미 선택된 탭 재클릭은 무시한다(종속 필드가 초기화되는 것을 방지)
          if (value === field.value) return;
          field.onChange(value);
          onValueChange?.(value);
        }}
      />
    </FormRow>
  );
}

// ============================================
// 멀티 셀렉트 (뱃지 표시)
// ============================================

/** 뱃지 색상 순환 */
const MULTI_SELECT_BADGE_VARIANTS: badgeVariantProps[] = [
  "pink",
  "yellow",
  "green",
  "skyblue",
  "violet",
  "lime",
  "purple",
];

export interface FormMultiSelectProps {
  name: string;
  icon: React.ReactNode;
  label: string;
  placeholder?: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
  isLoading?: boolean;
  tooltip?: React.ReactNode;
  required?: boolean;
}

/** 멀티 셀렉트 입력 필드 (선택 항목을 뱃지로 표시) */
export function FormMultiSelect({
  name,
  icon,
  label,
  placeholder,
  options,
  disabled = false,
  isLoading = false,
  tooltip,
  required = false,
}: FormMultiSelectProps) {
  const { field, fieldState } = useController({ name });
  const [open, setOpen] = React.useState(false);

  const fieldRef = useFieldRef(field);

  const selectedValues: string[] = field.value ?? [];

  const handleItemClick = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v: string) => v !== value)
      : [...selectedValues, value];
    field.onChange(newValues.length > 0 ? newValues : []);
  };

  const getBadgeVariant = (value: string) => {
    const idx = options.findIndex((o) => o.value === value);
    return MULTI_SELECT_BADGE_VARIANTS[
      idx >= 0 ? idx % MULTI_SELECT_BADGE_VARIANTS.length : 0
    ];
  };

  if (isLoading) {
    return <FormRow icon={icon} label={label} isLoading tooltip={tooltip} />;
  }

  if (disabled) {
    return (
      <FormRow icon={icon} label={label} tooltip={tooltip}>
        <div className="flex-1 min-h-6 flex items-center gap-1 flex-wrap">
          {selectedValues.length > 0 ? (
            selectedValues.map((val: string) => {
              const opt = options.find((o) => o.value === val);
              return (
                <Badge key={val} variant={getBadgeVariant(val)} size="sm">
                  {opt?.label ?? val}
                </Badge>
              );
            })
          ) : (
            <span className="typo-body-2-medium text-labels-primary">-</span>
          )}
        </div>
      </FormRow>
    );
  }

  return (
    <FormRow
      icon={icon}
      label={label}
      error={fieldState.invalid && fieldState.error}
      tooltip={tooltip}
      required={required}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={fieldRef}
            type="button"
            className={cn(
              "w-full min-h-8 flex items-center gap-1 px-2",
              "inset-border-solid-normal bg-static-white rounded-sm",
              "cursor-pointer",
              fieldState.invalid && "inset-border-error",
            )}
          >
            <div className="flex-1 flex items-center gap-1 flex-wrap py-1">
              {selectedValues.length > 0 ? (
                selectedValues.map((val: string) => {
                  const opt = options.find((o) => o.value === val);
                  return (
                    <Badge key={val} variant={getBadgeVariant(val)} size="sm">
                      {opt?.label ?? val}
                    </Badge>
                  );
                })
              ) : (
                <span className="typo-body-2-medium text-labels-assistive">
                  {placeholder ?? "선택해 주세요."}
                </span>
              )}
            </div>
            <ChevronDown
              size={24}
              color="labels-alternative"
              className="shrink-0"
            />
          </button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className={cn(
              "bg-static-white text-labels-primary",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2",
              "z-50 w-60 overflow-hidden rounded-xl",
              "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
            )}
            sideOffset={4}
            align="start"
          >
            <DropdownScrollArea
              className="max-h-70"
              contentClassName="flex flex-col gap-1 px-2 py-3"
            >
              {options.map((option) => {
                const isChecked = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleItemClick(option.value)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg h-8 px-2 py-1",
                      "cursor-pointer outline-none select-none",
                      "hover:bg-fills-primary focus:bg-fills-primary",
                    )}
                  >
                    <div className="shrink-0 size-6 flex items-center justify-center">
                      <div
                        className={cn(
                          "size-5 rounded-sm flex items-center justify-center transition-colors",
                          isChecked
                            ? "bg-primary-normal"
                            : "bg-fills-light inset-border-double",
                        )}
                      >
                        {isChecked && <Check size={16} color="static-white" />}
                      </div>
                    </div>
                    <span className="flex-1 typo-label-1-semibold text-labels-primary text-left truncate">
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </DropdownScrollArea>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </FormRow>
  );
}

export interface FormToggleRowProps {
  /** react-hook-form 필드 이름 (boolean) */
  name: string;
  icon: React.ReactNode;
  label: string;
  /** 토글 ON 시 렌더링할 추가 컨텐츠 */
  children?: React.ReactNode;
  disabled?: boolean;
  /**
   * 조회 모드 — 행 높이를 다른 텍스트 행(24px)에 맞춘다.
   * 개별 토글이 잠긴 것(`disabled`)과는 구분한다: 수정 모드에서 일부 토글만 잠겨도
   * 행 높이는 32px 그대로여야 한다 (공용/비공용을 바꿀 때 행 크기가 흔들리지 않게)
   */
  readOnly?: boolean;
  /** 라벨 옆 ⓘ 안내 (HintTooltip 등) */
  tooltip?: React.ReactNode;
  /** 필수 항목 표시 (*) — FormRow 와 같은 표기 */
  required?: boolean;
}

/**
 * 토글 스위치가 포함된 폼 행.
 * 토글이 활성화되면 children을 확장하여 표시한다.
 */
export function FormToggleRow({
  name,
  icon,
  label,
  children,
  disabled = false,
  readOnly = false,
  tooltip,
  required = false,
}: FormToggleRowProps) {
  const { field } = useController({ name });
  const checked = !!field.value;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2 w-full">
        <div
          className={cn(
            "flex items-center gap-1.5 shrink-0",
            readOnly ? "h-6" : "h-8",
          )}
        >
          {icon}
          <span className="typo-body-2-medium text-labels-alternative w-30 flex gap-1 items-center">
            {required ? label + "*" : label}
            {tooltip}
          </span>
        </div>
        <Switch
          checked={checked}
          onCheckedChange={field.onChange}
          disabled={disabled}
        />
      </div>
      {checked && children && (
        <div className="ml-37.5 shrink-0">{children}</div>
      )}
    </div>
  );
}
